import { useMemo, useEffect, type FC } from 'react';
import { useSelector } from 'react-redux';
import { useFragment, graphql } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import { stepInteractionConstants } from 'dibs-tracking';

const { STEP_INTERACTION_GLOBAL_MESSAGE_BANNER_DISPLAYED } = stepInteractionConstants;

import { trackGeoLocationPromos } from 'dibs-display-promo-code/exports/promoHelpers';
import MessageBannerItem from './MessageBannerItem';
import {
    wasShown,
    setValuesToStorage,
    addToShownList,
    getPromoMessage,
} from './helpers/messageHelpers';
import {
    getMessageTitle,
    hasAnyText,
    trackMessageBanner,
    globalFallbackMessage,
    type MessageItem,
    type NoteObj,
} from './helpers/sharedMessageHelpers';
import { isPreview } from './helpers/previewHelpers';

import { type MessageBannerContent_viewer$key } from './__generated__/MessageBannerContent_viewer.graphql';
import { type MessageBannerContent_user$key } from './__generated__/MessageBannerContent_user.graphql';
import { type MessageBannerContent_messages$key } from './__generated__/MessageBannerContent_messages.graphql';

import style from './MessageBanner-style.scss';

type Props = {
    viewer?: MessageBannerContent_viewer$key;
    user?: MessageBannerContent_user$key;
    messages?: MessageBannerContent_messages$key;
    messagesToPreview?: NoteObj[];
    showPreviewMessage?: boolean;
    useGlobalFallbackMessage?: boolean;
    onMessageDisplay: ({ messageTitle }: { messageTitle: string }) => void;
};

const viewerFragment = graphql`
    fragment MessageBannerContent_viewer on Viewer {
        activePromotion(userId: $userId) {
            ...messageHelpers_promotion
        }
        expiringPromotion(userId: $userId) {
            ...messageHelpers_expiringPromotion
        }
        ...promoHelpers_trackGeoLocationPromos_viewer
    }
`;

const userFragment = graphql`
    fragment MessageBannerContent_user on User {
        ...messageHelpers_user
    }
`;

const messagesFragment = graphql`
    fragment MessageBannerContent_messages on MessageSetModule @relay(plural: true) {
        items {
            note {
                serviceId
                content {
                    title
                    message
                    cta {
                        text
                        url
                    }
                }
                anchors {
                    serviceId
                    content {
                        title
                        message
                        cta {
                            text
                            url
                        }
                    }
                }
            }
        }
    }
`;

const MessageBannerContent: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    messages: messagesRef,
    messagesToPreview,
    showPreviewMessage,
    useGlobalFallbackMessage,
    onMessageDisplay,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const messages = useFragment(messagesFragment, messagesRef);
    const currency =
        useSelector((state: { currency?: { currency: string } }) => state.currency?.currency) ||
        'USD';

    const intl = useIntl();

    const messageToShow = useMemo(() => {
        if (messagesToPreview && showPreviewMessage) {
            return messagesToPreview?.[0]?.note ? messagesToPreview[0] : null;
        }

        const promotion = viewer?.activePromotion;
        const expiringPromotion = viewer?.expiringPromotion;

        let promoMessage = null;
        if (user && promotion && expiringPromotion) {
            promoMessage = getPromoMessage({
                userRef: user,
                promotionRef: promotion,
                expiringPromotionRef: expiringPromotion,
                intl,
                defaultCurrency: currency,
            });
        }

        const message = messages?.[0]?.items?.[0];
        if (promoMessage) {
            return promoMessage;
        } else if (message && hasAnyText(message)) {
            return message;
        } else if (useGlobalFallbackMessage) {
            return globalFallbackMessage(intl);
        }
        return null;
    }, [
        viewer,
        user,
        messages,
        messagesToPreview,
        showPreviewMessage,
        useGlobalFallbackMessage,
        currency,
        intl,
    ]);

    useEffect(() => {
        if (messageToShow) {
            onMessageDisplay({ messageTitle: getMessageTitle(messageToShow as MessageItem) });

            if (!isPreview() && !wasShown(messageToShow)) {
                setValuesToStorage(messageToShow);
                addToShownList(messageToShow);

                trackMessageBanner({
                    messageItem: messageToShow,
                    action: 'banner displayed',
                    step_interaction_name: STEP_INTERACTION_GLOBAL_MESSAGE_BANNER_DISPLAYED,
                });
            }
        }
    }, [messageToShow, onMessageDisplay]);

    useEffect(() => {
        if (viewer) {
            trackGeoLocationPromos({ viewer });
        }
    }, [viewer]);

    if (!messageToShow) {
        return null;
    }

    return (
        <div className={style.carouselWrapper}>
            <div className={style.carousel}>
                <div className={style.carouselItems}>
                    <MessageBannerItem
                        key={`${messageToShow?.note?.serviceId}`}
                        messageItem={messageToShow}
                        onFocus={() => {}}
                    />
                </div>
            </div>
        </div>
    );
};

export default MessageBannerContent;
